import { Controller } from "@hotwired/stimulus"
import { formatFileSize } from "../../helpers/files"

export default class extends Controller {
  static targets = ["input", "list", "template", "error", "header"]
  static values = {
    removeLabel: { type: String, default: "" },
    undoLabel: { type: String, default: "" },
    removedStatus: { type: String, default: "" },
    keptStatus: { type: String, default: "" },
    errorMessage: { type: String, default: "" }
  }

  connect() {
    this.files = []
    this.renderNewFiles()
    this.initFileDrop()
    this.applyStatusVisibility()
    this.updateHeaderVisibility()
  }

  selectFiles(event) {
    this.clearInlineError()
    const errors = this.fileErrors()
    const incoming = this.filterBySize(Array.from(event.currentTarget.files || []))
    const merged = event.currentTarget.multiple ? [...this.files] : []

    incoming.forEach((file) => {
      const exists = merged.some((existing) => (
        existing.name === file.name &&
        existing.size === file.size &&
        existing.lastModified === file.lastModified
      ))
      if (!exists) merged.push(file)
    })

    this.files = merged
    this.updateInputFiles()
    this.renderNewFiles(errors)
    this.scrollListIntoView()
  }

  removeNewFile(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index)
    if (Number.isNaN(index)) return

    const errors = this.fileErrors()
    this.files.splice(index, 1)
    this.updateInputFiles()
    this.renderNewFiles(errors)
  }

  toggleExisting(event) {
    event.preventDefault()
    const container = event.currentTarget.closest("[data-attachment-id]")
    const checkbox = container.querySelector('input[type="checkbox"]')

    checkbox.checked = !checkbox.checked
    const removed = !checkbox.checked
    container.classList.toggle("is-removed", removed)
    container.classList.toggle("tw-hidden", removed)

    const status = container.querySelector('[data-role="status"]')
    if (status && !this.hideStatus) {
      status.textContent = removed ? this.removedStatusValue : this.keptStatusValue
    }
  }

  renderNewFiles(errors = new Map()) {
    if (!this.hasListTarget) return
    const template = this.templateTarget.content.firstElementChild;

    this.listTarget.querySelectorAll('[data-attachment-kind="new"]').forEach((node) => node.remove())
    if (!template) return

    const fragment = document.createDocumentFragment()

    this.files.forEach((file, index) => {
      const item = template.cloneNode(true)
      const button = item.querySelector('[data-role="remove"]')
      const nameElement = item.querySelector('[data-role="name"]')

      item.dataset.attachmentKind = "new"
      nameElement.textContent = file.name
      nameElement.setAttribute("title", file.name)
      const status = item.querySelector('[data-role="status"]')
      if (status && !this.hideStatus) {
        status.textContent = this.keptStatusValue
      }
      if (status && this.hideStatus) {
        status.classList.add("tw-hidden")
        status.textContent = ""
      }
      item.querySelector('[data-role="size"]').textContent = this.formatFileSize(file.size)
      if (button) {
        button.dataset.index = index
      }
      const error = item.querySelector('[data-role="error"]')
      error.id = `${this.inputTarget.id}_file_${index}_error`
      const mark = errors.get(file)
      if (mark) {
        error.className = mark.className
        error.textContent = mark.textContent
      }

      fragment.appendChild(item)
    })

    this.listTarget.appendChild(fragment)
    this.updateHeaderVisibility()
  }

  fileErrors() {
    const errors = new Map()
    this.element.querySelectorAll('[data-attachment-kind="new"]').forEach((item, index) => {
      const error = item.querySelector('[data-role="error"]')
      if (!error.classList.contains("tw-hidden")) errors.set(this.files[index], error)
    })
    return errors
  }

  scrollListIntoView() {
    const scrollContainer = this.element.closest(".modal__body")
    if (!scrollContainer) return

    requestAnimationFrame(() => {
      scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: "smooth" })
    })
  }

  reset() {
    this.files = []
    this.updateInputFiles()
    this.renderNewFiles()
    this.clearInlineError()
  }

  get hideStatus() {
    return this.element.dataset.hideStatus === "true"
  }

  applyStatusVisibility() {
    if (!this.hideStatus || !this.hasListTarget) return
    this.listTarget.querySelectorAll('[data-role="status"]').forEach((node) => {
      node.classList.add("tw-hidden")
      node.textContent = ""
    })
  }

  updateHeaderVisibility() {
    if (!this.hasHeaderTarget || !this.hasListTarget) return
    const hasItems = this.listTarget.querySelectorAll("li").length > 0
    this.headerTarget.classList.toggle("tw-hidden", !hasItems)
  }

  updateInputFiles() {
    if (!this.hasInputTarget) return

    const dataTransfer = new DataTransfer()
    this.files.forEach((file) => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
  }

  formatFileSize(bytes) {
    return formatFileSize(bytes)
  }

  filterBySize(files) {
    const limit = Number(this.element.dataset.maxSize)

    return files.filter((file) => {
      if (file.size <= limit) return true

      this.showInlineError(this.sizeErrorMessage(file, limit))
      return false
    })
  }

  sizeErrorMessage(file, limit) {
    const fileSize = this.formatFileSize(file.size)
    const max = this.formatFileSize(limit)

    return this.errorMessageValue
      .replace("%{name}", file.name)
      .replace("%{size}", fileSize)
      .replace("%{max}", max)
  }

  showInlineError(message) {
    if (!message || !this.hasErrorTarget) return
    this.setError(message, false)
  }

  clearInlineError() {
    if (!this.hasErrorTarget) return

    this.setError("", true)
  }

  setError(text, hidden) {
    window.$(this.errorTarget).text(text)[hidden ? "addClass" : "removeClass"]("tw-hidden")
  }

  initFileDrop() {
    const dropTarget = this.element.querySelector(".md-file-drop[data-target-field]")
    if (!dropTarget || dropTarget.dataset.fileDropInit) return

    if (window.fileDropHandler?.initMdFileDrop) {
      fileDropHandler.initMdFileDrop(dropTarget)
      fileDropHandler.initFileDialogElements(dropTarget)
      dropTarget.dataset.fileDropInit = "true"
    }
  }
}
